/**
 * Sweep report — V109-5-4 / V109-5-5 (PRD G-7, G-8; ADR-0089). One result set, read two ways.
 *
 * For one program (a manifest with `features.transitionGuidance`: GovRAMP, FedRAMP 20x) and the
 * workspace's latest CLOSED sweep, `buildProgramSweepReport` returns:
 *   - stamps: the run (id, state, when, catalog version), the sweep it was compared with, and the
 *     sources it read (each child's status) — the freshness every view states;
 *   - `checked`: the program's own controls (D-103-14: its control set, GovRAMP's tier members) that
 *     the sweep's counted results reach through approved test maps (rule → check key → test →
 *     objective → requirement; ADR-0088 §6), each with the worst result: Not met > Couldn't check >
 *     Met > Not applicable. now / prev counts reconcile with the rows (`reconcileSweepReport`).
 *     Preview results (LM-21) are never counted — only their number is reported;
 *   - `changes`: the sweep's persisted drift events that reach this program, each with its cause in
 *     the closed set of three (your environment changed / our rules changed / GAC Pro couldn't read
 *     it), the resource, expected vs actual, the rule's remediation, and every program control it
 *     affects (GovRAMP and FedRAMP are listed separately — they never credit each other);
 *   - `drift`: per-category counts of those changes plus `unchanged` (reached keys not changed).
 *
 * Read-only. Every tenant read is keyed by workspace_id; control-plane content (test maps, rule
 * titles) is read from `content`. The caller resolves the program inside the session workspace.
 */
import { getProgramManifest } from '../programs/manifests.js';
import { isBaselineSource, selectRequirementSourceLeaf } from '../programs/requirement-source.js';
import { loadLedgerReach } from '../rules/coverage-ledger.js';
import { PREVIEW_DETAILS_MARK } from '../rules/preview.js';
import { indexSweepResults, listDriftEvents } from './drift-service.js';
import { getScanRun, listCollections } from './scan-run-service.js';
import { listProgramControls } from './program-control-service.js';
import { sqlIn } from '../utils/sql-in.js';
import { driftSubjectKey } from './drift-notification-service.js';
export const SWEEP_CHECK_STATES = ['met', 'not_met', 'couldnt_check', 'not_applicable'];
const RANK = { not_applicable: 0, met: 1, couldnt_check: 2, not_met: 3 };
const zeroCounts = () => ({ met: 0, not_met: 0, couldnt_check: 0, not_applicable: 0 });
const zeroDrift = () => ({ newly_failing: 0, newly_passing: 0, newly_applicable: 0, no_longer_applicable: 0, unevaluated: 0, unchanged: 0 });
/** A counted result's outcome as a control state (an accepted-risk exception is still not met). */
export function stateOfOutcome(o) {
    if (o === 'pass')
        return 'met';
    if (o === 'fail')
        return 'not_met';
    if (o === 'not_applicable')
        return 'not_applicable';
    return 'couldnt_check';
}
/** Drift cause → the closed set. An unevaluated change is "GAC Pro couldn't read it" whatever else is true. */
export function sweepCauseOf(category, cause) {
    if (category === 'unevaluated')
        return 'couldnt_read';
    if (cause === 'environment_change')
        return 'environment';
    if (cause === 'rule_change')
        return 'rules';
    return null;
}
export const isSweepProgram = (m) => !!m && m.features.transitionGuidance === true && !!m.frameworkId;
/**
 * Pure: per program requirement, the worst state over the results that reach it. `reach` maps a
 * check key to the requirement ids its approved tests reach; `controls` is the program's control set.
 */
export function requirementStates(results, reach, controls) {
    const out = new Map();
    for (const r of results) {
        const key = r.key.check_key;
        if (!key)
            continue;
        const s = stateOfOutcome(r.side.outcome);
        for (const req of reach.get(key) ?? []) {
            if (!controls.has(req))
                continue;
            const cur = out.get(req);
            if (!cur) {
                out.set(req, { state: s, checks: 1, last: r.observed_at });
                continue;
            }
            cur.checks++;
            if (RANK[s] > RANK[cur.state])
                cur.state = s;
            if (!cur.last || r.observed_at > cur.last)
                cur.last = r.observed_at;
        }
    }
    return out;
}
export const countStates = (states) => {
    const c = zeroCounts();
    for (const s of states)
        c[s.state]++;
    return c;
};
/** Reconciliation (acceptance): buckets = rows, and drift counts = change rows by category. */
export function reconcileSweepReport(r) {
    const errs = [];
    const rows = countStates(r.checked.requirements);
    for (const s of SWEEP_CHECK_STATES)
        if (rows[s] !== r.checked.now[s])
            errs.push(`checked.${s}: ${r.checked.now[s]} ≠ ${rows[s]} rows`);
    if (r.checked.prev) {
        const prevRows = countStates(r.checked.requirements.filter((q) => q.prev_state).map((q) => ({ state: q.prev_state })));
        for (const s of SWEEP_CHECK_STATES)
            if (prevRows[s] > r.checked.prev[s])
                errs.push(`prev.${s}: rows exceed the count`);
    }
    if (!r.truncated) {
        for (const cat of ['newly_failing', 'newly_passing', 'newly_applicable', 'no_longer_applicable', 'unevaluated']) {
            const n = r.changes.filter((c) => c.category === cat).length;
            if (n !== r.drift[cat])
                errs.push(`drift.${cat}: ${r.drift[cat]} ≠ ${n} rows`);
        }
    }
    return errs;
}
const stamp = (r) => (r ? {
    run_id: r.run_id, state: r.state, trigger: r.trigger, started_at: r.started_at, finished_at: r.finished_at, catalog_version: r.catalog_version,
} : null);
/** The workspace's latest closed sweep. */
export async function latestClosedSweep(db, workspaceId) {
    return (await db.query(`SELECT run_id, workspace_id, kind, trigger, catalog_version, state, started_at, finished_at, deadline_at, started_by, summary_json
       FROM scan_runs WHERE workspace_id = ? AND kind = 'sweep' AND state <> 'running' ORDER BY started_at DESC, run_id DESC LIMIT 1`, [workspaceId])).results[0] ?? null;
}
/** The sweep a run was compared with: the newest of its per-source `compared_against` runs. */
async function comparedWith(db, run) {
    let s = {};
    try {
        s = JSON.parse(run.summary_json || '{}');
    }
    catch {
        s = {};
    }
    const ids = [...new Set(Object.values(s.compared_against ?? {}).filter((x) => !!x))];
    let best = null;
    for (const id of ids) {
        const r = await getScanRun(db, run.workspace_id, id);
        if (r && (!best || r.started_at > best.started_at))
            best = r;
    }
    return best;
}
const optTier = (json) => {
    try {
        const o = JSON.parse(json || '{}');
        return typeof o.tier === 'string' ? o.tier : null;
    }
    catch {
        return null;
    }
};
/**
 * V109-5 built-UI #1: the program's CURRENT tier — the latest period's (a tier change opens a new period,
 * v10.3), else the profile's — the tier the GovRAMP overview's readiness is computed for.
 */
const tierOf = (p) => optTier(p.period_options_json) ?? optTier(p.program_options_json);
/** A program's control set as the sweep counts it: GovRAMP keeps the members of the tier being assessed. */
async function controlSetOf(p, m, list) {
    let rows = [];
    try {
        rows = await list(p.profile_id);
    }
    catch {
        rows = [];
    }
    // v10.6 (ADR-0079): the profile's leaf source (keyed sources resolved); only a baseline leaf has tiers.
    let options = {};
    try {
        options = JSON.parse(p.program_options_json || '{}');
    }
    catch {
        options = {};
    }
    let leaf;
    try {
        leaf = selectRequirementSourceLeaf(m.requirementSource, { options });
    }
    catch {
        leaf = undefined;
    }
    const tiers = leaf && isBaselineSource(leaf) ? leaf : null;
    const tierKey = tiers?.tierOption ? tierOf(p) : null;
    const tier = tierKey ? (tiers?.tierAliases?.[tierKey] ?? tierKey) : null;
    const kept = tier ? rows.filter((r) => !r.tiers.length || r.tiers.includes(tier)) : rows;
    return new Map(kept.map((r) => [r.requirement_id, { requirement_id: r.requirement_id, ref: r.ref, title: r.title }]));
}
export const MAX_SWEEP_CHANGES = 300;
/** What the sweep read, per child (the "Scope" disclosure beside every freshness line). */
async function sourceStamps(db, workspaceId, children) {
    const kinds = new Map((await db.query(`SELECT connector_id, kind FROM evidence_connectors WHERE workspace_id = ?`, [workspaceId])).results.map((r) => [r.connector_id, r.kind]));
    return children.map((c) => ({
        source_kind: c.source_kind, connector_id: c.connector_id, connector_kind: c.connector_id ? kinds.get(c.connector_id) ?? null : null,
        target: c.target, environment: c.environment, status: c.status,
    }));
}
export async function buildProgramSweepReport(db, content, input, deps = {}) {
    const now = deps.now ?? new Date();
    const reachOf = deps.reach ?? ((keys) => loadLedgerReach(content, keys));
    const listControls = deps.programControls ?? (async (profileId) => (await listProgramControls(db, input.workspace_id, profileId)).controls.map((c) => ({ requirement_id: c.requirement_id, ref: c.ref, title: c.title, tiers: c.tiers })));
    const profiles = (await db.query(`SELECT p.profile_id, p.program, p.program_options_json,
            (SELECT ap.program_options_json FROM audit_periods ap WHERE ap.workspace_id = p.workspace_id AND ap.profile_id = p.profile_id
              ORDER BY ap.period_start DESC, ap.created_at DESC LIMIT 1) AS period_options_json
       FROM certification_profiles p WHERE p.workspace_id = ? AND p.status = 'active' ORDER BY p.created_at, p.profile_id`, [input.workspace_id])).results;
    const self = profiles.find((p) => p.profile_id === input.profile_id);
    const m = self ? getProgramManifest(self.program) : undefined;
    if (!self || !m || !isSweepProgram(m))
        throw new SweepReportError('not_found', 'This program has no sweep report.');
    const unit = m.features.persistentValidation ? 'ksi' : 'control';
    // Every sweep program's control set (affects lists each program separately).
    const sets = new Map();
    for (const p of profiles) {
        const pm = getProgramManifest(p.program);
        if (!isSweepProgram(pm))
            continue;
        sets.set(p.profile_id, { profile: p, manifest: pm, controls: await controlSetOf(p, pm, listControls) });
    }
    const mine = sets.get(self.profile_id).controls;
    const empty = {
        program: self.program, profile_id: self.profile_id, unit, run: null, compared_with: null, sources: [], controls_total: mine.size, tier: tierOf(self),
        checked: { now: zeroCounts(), prev: null, requirements: [] }, preview: 0, drift: zeroDrift(), changes: [], truncated: false, generated_at: now.toISOString(),
    };
    const run = await latestClosedSweep(db, input.workspace_id);
    if (!run)
        return empty;
    const prevRun = await comparedWith(db, run);
    const children = await listCollections(db, input.workspace_id, run.run_id);
    const sources = await sourceStamps(db, input.workspace_id, children);
    const curr = await indexSweepResults(db, input.workspace_id, run.run_id, children);
    const prev = prevRun ? await indexSweepResults(db, input.workspace_id, prevRun.run_id, await listCollections(db, input.workspace_id, prevRun.run_id)) : null;
    const events = await listDriftEvents(db, input.workspace_id, run.run_id);
    // Preview rows of this run (LM-21): counted only as a number.
    const previewRows = (await db.query(`SELECT check_key FROM control_checks WHERE workspace_id = ? AND run_id = ? AND COALESCE(rule_id, '') <> '' AND COALESCE(details_json, '') LIKE ?`, [input.workspace_id, run.run_id, `%${PREVIEW_DETAILS_MARK}%`])).results;
    const keys = new Set();
    for (const r of curr.values())
        if (r.key.check_key)
            keys.add(r.key.check_key);
    for (const r of prev?.values() ?? [])
        if (r.key.check_key)
            keys.add(r.key.check_key);
    for (const e of events)
        if (e.check_key)
            keys.add(e.check_key);
    for (const p of previewRows)
        if (p.check_key)
            keys.add(p.check_key);
    const reach = new Map();
    for (const v of keys.size ? await reachOf([...keys]) : [])
        reach.set(v.check_key, [...new Set([...(reach.get(v.check_key) ?? []), v.requirement_id])]);
    const reachesMine = (k) => !!k && (reach.get(k) ?? []).some((id) => mine.has(id));
    const nowStates = requirementStates(curr.values(), reach, mine);
    const prevStates = prev ? requirementStates(prev.values(), reach, mine) : null;
    const requirements = [...nowStates].map(([id, s]) => ({
        requirement_id: id, ref: mine.get(id).ref, title: mine.get(id).title, state: s.state, prev_state: prevStates?.get(id)?.state ?? null,
        checks: s.checks, last_reading_at: s.last,
    })).sort((a, b) => RANK[b.state] - RANK[a.state] || a.ref.localeCompare(b.ref, undefined, { numeric: true }));
    const changedKeys = new Set(events.map((e) => e.key_hash));
    const ruleTitles = await ruleTitlesOf(content, events);
    const resources = await resourcesOf(db, input.workspace_id, events);
    const affectsOf = (checkKey) => {
        const out = [];
        for (const { profile, controls } of sets.values()) {
            for (const id of reach.get(checkKey ?? '') ?? []) {
                const c = controls.get(id);
                if (c)
                    out.push({ program: profile.program, profile_id: profile.profile_id, requirement_id: id, ref: c.ref, title: c.title });
            }
        }
        // This program first, then the others in manifest order.
        const rank = (a) => (a.profile_id === self.profile_id ? -1 : getProgramManifest(a.program)?.order ?? 99);
        return out.sort((a, b) => rank(a) - rank(b) || a.ref.localeCompare(b.ref, undefined, { numeric: true }));
    };
    const drift = zeroDrift();
    const all = [];
    const findings = await findingsOf(db, input.workspace_id, events.filter((e) => e.category === 'newly_failing' && reachesMine(e.check_key)));
    for (const e of events) {
        if (!reachesMine(e.check_key))
            continue;
        drift[e.category]++;
        all.push({ ...changeOf(e, ruleTitles, resources, affectsOf(e.check_key)), ...(findings.has(e.event_id) ? { finding: findings.get(e.event_id) } : {}) });
    }
    for (const [h, r] of curr)
        if (!changedKeys.has(h) && reachesMine(r.key.check_key))
            drift.unchanged++;
    const ORDER = ['newly_failing', 'unevaluated', 'newly_passing', 'newly_applicable', 'no_longer_applicable'];
    all.sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category) || (a.resource?.name ?? '').localeCompare(b.resource?.name ?? '') || a.id.localeCompare(b.id));
    const limit = Math.min(Math.max(deps.limit ?? MAX_SWEEP_CHANGES, 1), MAX_SWEEP_CHANGES);
    return {
        ...empty,
        run: stamp(run), compared_with: stamp(prevRun), sources,
        checked: { now: countStates(requirements), prev: prevStates ? countStates(prevStates.values()) : null, requirements },
        preview: previewRows.filter((p) => reachesMine(p.check_key)).length,
        drift, changes: all.slice(0, limit), truncated: all.length > limit,
    };
}
/** One call for every entry point: the run's stamps, the inventory counts and each sweep program's change counts. */
export async function buildSweepSummary(db, content, input, deps = {}) {
    const run = await latestClosedSweep(db, input.workspace_id);
    const prevRun = run ? await comparedWith(db, run) : null;
    const count = async (sql, params) => Number((await db.query(sql, params)).results[0]?.n ?? 0);
    const found = await count(`SELECT COUNT(*) AS n FROM infra_resources WHERE workspace_id = ? AND gone_at IS NULL`, [input.workspace_id]);
    const since = prevRun?.finished_at ?? null;
    const newSince = since ? await count(`SELECT COUNT(*) AS n FROM infra_resources WHERE workspace_id = ? AND gone_at IS NULL AND first_seen_at > ?`, [input.workspace_id, since]) : 0;
    const clues = await count(`SELECT COUNT(*) AS n FROM infra_unresolved_clues WHERE workspace_id = ? AND status = 'open' AND gone_at IS NULL`, [input.workspace_id]);
    const profiles = (await db.query(`SELECT profile_id, program FROM certification_profiles WHERE workspace_id = ? AND status = 'active' ORDER BY created_at, profile_id`, [input.workspace_id])).results
        .filter((p) => isSweepProgram(getProgramManifest(p.program)))
        .sort((a, b) => (getProgramManifest(a.program).order - getProgramManifest(b.program).order));
    const programs = [];
    if (run) {
        for (const p of profiles) {
            const r = await buildProgramSweepReport(db, content, { workspace_id: input.workspace_id, profile_id: p.profile_id }, deps);
            const total = r.drift.newly_failing + r.drift.newly_passing + r.drift.newly_applicable + r.drift.no_longer_applicable + r.drift.unevaluated;
            programs.push({ program: p.program, profile_id: p.profile_id, unit: r.unit, broke: r.drift.newly_failing, couldnt_check: r.drift.unevaluated, newly_passing: r.drift.newly_passing, changes: total });
        }
    }
    let verdicts = null;
    try {
        verdicts = deps.verdictsNeedingYou ? await deps.verdictsNeedingYou() : null;
    }
    catch {
        verdicts = null;
    }
    const sources = run ? await sourceStamps(db, input.workspace_id, await listCollections(db, input.workspace_id, run.run_id)) : [];
    return { run: stamp(run), compared_with: stamp(prevRun), sources, found, new_since_last: newSince, clues_open: clues, verdicts_need_you: verdicts, programs };
}
export class SweepReportError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SweepReportError';
    }
}
const parse = (s) => { if (s === null)
    return null; try {
    return JSON.parse(s);
}
catch {
    return null;
} };
function changeOf(e, titles, resources, affects) {
    const t = (e.curr_rule_version_id && titles.get(`v:${e.curr_rule_version_id}`)) || (e.rule_id && titles.get(`r:${e.rule_id}`)) || null;
    let flags = [];
    try {
        const f = JSON.parse(e.flags_json);
        if (Array.isArray(f))
            flags = f.filter((x) => typeof x === 'string');
    }
    catch {
        flags = [];
    }
    return {
        id: e.event_id, category: e.category, cause: sweepCauseOf(e.category, e.cause), reason: e.reason, flags,
        rule_id: e.rule_id, check_key: e.check_key, title: t?.title ?? null, remediation: t?.remediation ?? null,
        resource: e.resource_id ? resources.get(e.resource_id) ?? null : null, connector_id: e.connector_id, environment: e.environment,
        prev_outcome: e.prev_outcome, curr_outcome: e.curr_outcome, expected: parse(e.expected_json), actual: parse(e.actual_json), affects,
    };
}
/** Rule titles (by rule id) and remediation (by rule version) from the control plane. Bounded by one sweep's events. */
async function ruleTitlesOf(content, events) {
    const out = new Map();
    const ruleIds = [...new Set(events.map((e) => e.rule_id).filter((x) => !!x))];
    const versionIds = [...new Set(events.map((e) => e.curr_rule_version_id).filter((x) => !!x))];
    try {
        if (ruleIds.length) {
            const q = sqlIn(ruleIds);
            for (const r of (await content.query(`SELECT rule_id, title FROM check_rules WHERE rule_id IN ${q.sql}`, q.params)).results) {
                out.set(`r:${r.rule_id}`, { title: r.title, remediation: null });
            }
        }
        if (versionIds.length) {
            const q = sqlIn(versionIds);
            for (const v of (await content.query(`SELECT rule_version_id, content_json FROM check_rule_versions WHERE rule_version_id IN ${q.sql}`, q.params)).results) {
                let c = {};
                try {
                    c = JSON.parse(v.content_json);
                }
                catch {
                    c = {};
                }
                out.set(`v:${v.rule_version_id}`, { title: typeof c.title === 'string' ? c.title : '', remediation: typeof c.remediation === 'string' ? c.remediation : null });
            }
        }
    }
    catch { /* titles are optional (a control plane without the check library) */ }
    for (const [k, v] of out)
        if (k.startsWith('v:') && !v.title) {
            const e = events.find((x) => `v:${x.curr_rule_version_id}` === k);
            const r = e?.rule_id ? out.get(`r:${e.rule_id}`) : undefined;
            out.set(k, { title: r?.title ?? '', remediation: v.remediation });
        }
    return out;
}
/**
 * The finding each newly failing change keeps open (built-UI #7): a resource-scoped one by its subject key
 * (seam 6, `subject:<key>`), else the one the change's own check opened. Workspace-scoped; open ones first.
 */
async function findingsOf(db, workspaceId, events) {
    const out = new Map();
    if (!events.length)
        return out;
    const keys = new Map();
    for (const e of events)
        if (e.resource_id)
            keys.set(e.event_id, `subject:${await driftSubjectKey(workspaceId, e)}`);
    const checks = events.map((e) => e.curr_check_id).filter((x) => !!x);
    const rows = [];
    try {
        const dk = [...new Set(keys.values())];
        if (dk.length) {
            const q = sqlIn(dk);
            rows.push(...(await db.query(`SELECT finding_id, status, external_ticket_id, external_ticket_url, dedup_key, check_id FROM findings WHERE workspace_id = ? AND dedup_key IN ${q.sql} ORDER BY opened_at DESC`, [workspaceId, ...q.params])).results);
        }
        if (checks.length) {
            const q = sqlIn(checks);
            rows.push(...(await db.query(`SELECT finding_id, status, external_ticket_id, external_ticket_url, dedup_key, check_id FROM findings WHERE workspace_id = ? AND check_id IN ${q.sql} ORDER BY opened_at DESC`, [workspaceId, ...q.params])).results);
        }
    }
    catch {
        return out;
    }
    const OPEN = new Set(['open', 'acknowledged']);
    const pick = (list) => list.find((r) => OPEN.has(r.status)) ?? list[0] ?? null;
    for (const e of events) {
        const k = keys.get(e.event_id);
        const r = pick(rows.filter((x) => (k && x.dedup_key === k) || (e.curr_check_id && x.check_id === e.curr_check_id)));
        if (r)
            out.set(e.event_id, { finding_id: r.finding_id, status: r.status, external_ticket_id: r.external_ticket_id, external_ticket_url: r.external_ticket_url });
    }
    return out;
}
async function resourcesOf(db, workspaceId, events) {
    const ids = [...new Set(events.map((e) => e.resource_id).filter((x) => !!x))];
    const out = new Map();
    if (!ids.length)
        return out;
    const q = sqlIn(ids);
    const rows = (await db.query(`SELECT r.resource_id, r.name, r.resource_type, r.environment, r.account_ref, i.provider_key, i.product
       FROM infra_resources r LEFT JOIN infra_inventory_items i ON i.item_id = r.item_id AND i.workspace_id = r.workspace_id
      WHERE r.workspace_id = ? AND r.resource_id IN ${q.sql}`, [workspaceId, ...q.params])).results;
    for (const r of rows)
        out.set(r.resource_id, r);
    return out;
}
//# sourceMappingURL=sweep-report-service.js.map